/**
 * Encodage binaire des dessins (module pur, teste).
 *
 * Le serveur ne decode jamais ces buffers : il les relaie comme des blobs
 * opaques. L'encodage et le decodage vivent donc entierement cote client, mais
 * le format est dans shared/ parce qu'il est le contrat entre le dessinateur et
 * le devineur.
 *
 * Layout (little-endian) :
 *
 *   En-tete
 *     uint8   version                (= FORMAT_VERSION)
 *     uint16  strokeCount
 *   Puis, par trait :
 *     uint8   tool                   (index dans TOOL_ORDER)
 *     uint8   colorIndex             (index dans la palette du tableau)
 *     uint8   size                   (unites zone, cf. ZONE_UNITS)
 *     uint16  pointCount
 *     int16[] deltas                 (x, y entrelaces, quantifies)
 *
 * Les points sont en coordonnees normalisees 0-1 *relatives a la zone*, et
 * quantifies sur la grille DRAWING_GRID (4096). Le premier point d'un trait est
 * un delta depuis l'origine (0, 0) ; les suivants sont des deltas depuis le
 * point precedent. Un dessin typique tient largement sous 4 Ko.
 */
import { DRAWING_GRID } from './constants.js';
/** Erreur levee par decodeDrawing sur un buffer malforme. */
export class DrawingFormatError extends Error {
    name = 'DrawingFormatError';
}
export const FORMAT_VERSION = 1;
/** L'ordre fait foi : l'index est ce qui part sur le reseau. Ne jamais reordonner. */
export const TOOL_ORDER = ['brush', 'fill', 'eraser'];
const HEADER_BYTES = 3;
const STROKE_HEADER_BYTES = 5;
const BYTES_PER_POINT = 4;
const MAX_UINT16 = 0xffff;
/**
 * Marge de debordement autorisee autour de la zone. Un trait commence dans la
 * zone peut sortir de quelques pourcents : sans marge, la quantification le
 * ferait s'ecraser contre le bord, ce qui se voit a l'ecran.
 */
export const OVERDRAW_MARGIN = 0.25;
const QUANT_MIN = Math.round(-OVERDRAW_MARGIN * DRAWING_GRID);
const QUANT_MAX = Math.round((1 + OVERDRAW_MARGIN) * DRAWING_GRID);
function clamp(value, min, max) {
    return value < min ? min : value > max ? max : value;
}
function quantize(value, axis) {
    if (!Number.isFinite(value)) {
        throw new DrawingFormatError(`coordonnee ${axis} non finie : ${String(value)}`);
    }
    return clamp(Math.round(value * DRAWING_GRID), QUANT_MIN, QUANT_MAX);
}
function toolIndex(tool) {
    const index = TOOL_ORDER.indexOf(tool);
    if (index < 0)
        throw new DrawingFormatError(`outil inconnu : ${String(tool)}`);
    return index;
}
function byteOrThrow(value, label) {
    if (!Number.isInteger(value) || value < 0 || value > 255) {
        throw new DrawingFormatError(`${label} doit etre un entier 0-255, recu ${String(value)}`);
    }
    return value;
}
/** Taille exacte, en octets, du buffer que produirait encodeDrawing. */
export function encodedSize(drawing) {
    let bytes = HEADER_BYTES;
    for (const stroke of drawing) {
        bytes += STROKE_HEADER_BYTES + stroke.points.length * BYTES_PER_POINT;
    }
    return bytes;
}
export function encodeDrawing(drawing) {
    if (drawing.length > MAX_UINT16) {
        throw new DrawingFormatError(`trop de traits : ${drawing.length} > ${MAX_UINT16}`);
    }
    const buffer = new ArrayBuffer(encodedSize(drawing));
    const view = new DataView(buffer);
    let offset = 0;
    view.setUint8(offset, FORMAT_VERSION);
    offset += 1;
    view.setUint16(offset, drawing.length, true);
    offset += 2;
    for (const stroke of drawing) {
        if (stroke.points.length > MAX_UINT16) {
            throw new DrawingFormatError(`trop de points : ${stroke.points.length} > ${MAX_UINT16}`);
        }
        view.setUint8(offset, toolIndex(stroke.tool));
        offset += 1;
        view.setUint8(offset, byteOrThrow(stroke.colorIndex, 'colorIndex'));
        offset += 1;
        view.setUint8(offset, byteOrThrow(stroke.size, 'size'));
        offset += 1;
        view.setUint16(offset, stroke.points.length, true);
        offset += 2;
        let previousX = 0;
        let previousY = 0;
        for (const point of stroke.points) {
            const x = quantize(point.x, 'x');
            const y = quantize(point.y, 'y');
            view.setInt16(offset, x - previousX, true);
            offset += 2;
            view.setInt16(offset, y - previousY, true);
            offset += 2;
            previousX = x;
            previousY = y;
        }
    }
    return buffer;
}
/**
 * Reduit un dessin jusqu'a ce qu'il tienne dans `maxBytes`.
 *
 * Filet de securite : le serveur rejette tout dessin au-dela de
 * MAX_DRAWING_BYTES, et un dessin rejete fait perdre la manche a son auteur. Le
 * pas d'echantillonnage du client garde les tailles basses en pratique, mais
 * rien n'empeche un joueur tres bavard de le depasser : mieux vaut une retouche
 * legerement decimee qu'une retouche perdue.
 *
 * La decimation garde toujours le premier et le dernier point de chaque trait,
 * donc la forme generale et les extremites sont preservees.
 */
export function fitToBudget(drawing, maxBytes) {
    if (encodedSize(drawing) <= maxBytes)
        return drawing;
    let current = [...drawing];
    // Chaque passe divise le nombre de points par deux : la convergence est
    // logarithmique, la borne evite toute boucle infinie.
    for (let pass = 0; pass < 16; pass += 1) {
        current = current.map((stroke) => {
            if (stroke.points.length <= 2)
                return stroke;
            const kept = [];
            for (let i = 0; i < stroke.points.length; i += 2)
                kept.push(stroke.points[i]);
            const last = stroke.points[stroke.points.length - 1];
            if (kept[kept.length - 1] !== last)
                kept.push(last);
            return { ...stroke, points: kept };
        });
        if (encodedSize(current) <= maxBytes)
            return current;
    }
    return current;
}
/**
 * Tronque un dessin a une fraction de son trace total, en coupant le dernier
 * trait au milieu si besoin. C'est ce qui donne au replay l'impression d'une
 * main qui dessine plutot que de traits qui apparaissent d'un bloc.
 *
 * Le budget se compte en points et non en traits : sans ca, un trait long
 * passerait aussi vite qu'un point isole.
 */
export function sliceDrawing(drawing, progress) {
    if (progress >= 1)
        return drawing;
    if (progress <= 0)
        return [];
    let budget = drawing.reduce((sum, stroke) => sum + Math.max(stroke.points.length, 1), 0) * progress;
    const out = [];
    for (const stroke of drawing) {
        const length = Math.max(stroke.points.length, 1);
        if (budget >= length) {
            out.push(stroke);
            budget -= length;
            continue;
        }
        const keep = Math.floor(budget);
        // Le pot de peinture est atomique : il est applique ou il ne l'est pas.
        if (stroke.tool === 'fill') {
            if (keep >= 1)
                out.push(stroke);
            break;
        }
        if (keep >= 1)
            out.push({ ...stroke, points: stroke.points.slice(0, keep) });
        break;
    }
    return out;
}
export function decodeDrawing(buffer) {
    const view = new DataView(buffer);
    if (buffer.byteLength < HEADER_BYTES) {
        throw new DrawingFormatError(`buffer trop court : ${buffer.byteLength} octet(s), en-tete de ${HEADER_BYTES} attendu`);
    }
    let offset = 0;
    const version = view.getUint8(offset);
    offset += 1;
    if (version !== FORMAT_VERSION) {
        throw new DrawingFormatError(`version inconnue : ${version} (attendu ${FORMAT_VERSION})`);
    }
    const strokeCount = view.getUint16(offset, true);
    offset += 2;
    const strokes = [];
    for (let index = 0; index < strokeCount; index += 1) {
        if (offset + STROKE_HEADER_BYTES > buffer.byteLength) {
            throw new DrawingFormatError(`en-tete du trait ${index} tronque`);
        }
        const tool = TOOL_ORDER[view.getUint8(offset)];
        offset += 1;
        if (tool === undefined) {
            throw new DrawingFormatError(`outil inconnu au trait ${index}`);
        }
        const colorIndex = view.getUint8(offset);
        offset += 1;
        const size = view.getUint8(offset);
        offset += 1;
        const pointCount = view.getUint16(offset, true);
        offset += 2;
        const pointBytes = pointCount * BYTES_PER_POINT;
        if (offset + pointBytes > buffer.byteLength) {
            throw new DrawingFormatError(`points du trait ${index} tronques : ${pointCount} annonce(s), ` +
                `${buffer.byteLength - offset} octet(s) restant(s)`);
        }
        const points = [];
        let x = 0;
        let y = 0;
        for (let p = 0; p < pointCount; p += 1) {
            x += view.getInt16(offset, true);
            offset += 2;
            y += view.getInt16(offset, true);
            offset += 2;
            points.push({ x: x / DRAWING_GRID, y: y / DRAWING_GRID });
        }
        strokes.push({ tool, colorIndex, size, points });
    }
    if (offset !== buffer.byteLength) {
        throw new DrawingFormatError(`${buffer.byteLength - offset} octet(s) en trop apres le dernier trait`);
    }
    return strokes;
}
//# sourceMappingURL=drawing.js.map